import { useCallback, useEffect, useRef, useState } from "react";

const VISIBLE_CLASS = "tts-widget-visible";

/** Fade in the TTS widget by adding the visibility class. */
function fadeInWidget(widget: HTMLElement) {
  widget.style.removeProperty("display");
  // rAF ensures the browser has rendered the element before starting the transition
  requestAnimationFrame(() => {
    widget.classList.add(VISIBLE_CLASS);
  });
}

/** Fade out the TTS widget, then set display:none after the transition. */
function fadeOutWidget(widget: HTMLElement) {
  widget.classList.remove(VISIBLE_CLASS);
  const onEnd = () => {
    if (!widget.classList.contains(VISIBLE_CLASS)) {
      widget.style.display = "none";
    }
    widget.removeEventListener("transitionend", onEnd);
  };
  widget.addEventListener("transitionend", onEnd);
}

/**
 * Hook to manage the TTS (Text-to-Speech) sticky widget state and interactions.
 * Handles visibility toggling, DOM widget display (with fade), and sync with BeyondWords player events.
 */
export function useTtsWidget(articleId: string) {
  const [ttsVisible, setTtsVisible] = useState(() => {
    return (
      typeof window !== "undefined" &&
      window.beyondWordsPlayerInstances?.[articleId]?.playbackState ===
        "playing"
    );
  });
  const [isPlayerReady, setIsPlayerReady] = useState(() => {
    return (
      typeof window !== "undefined" &&
      !!window.beyondWordsPlayerInstances?.[articleId]
    );
  });

  const ttsVisibleRef = useRef(ttsVisible);
  ttsVisibleRef.current = ttsVisible;

  // Perform DOM side-effects whenever ttsVisible changes
  useEffect(() => {
    if (typeof document === "undefined") return;
    const widget = document.getElementById(`tts-sticky-widget-${articleId}`);
    if (!widget) return;

    if (ttsVisible) {
      fadeInWidget(widget);
    } else {
      fadeOutWidget(widget);
    }
  }, [ttsVisible, articleId]);

  // Listen for player readiness and external visibility changes
  useEffect(() => {
    const handleReady = () => {
      setIsPlayerReady(true);
    };
    const handleVisibilityChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ visible: boolean }>;
      setTtsVisible(customEvent.detail.visible);
    };

    window.addEventListener(`beyondwords-ready-${articleId}`, handleReady);
    window.addEventListener(
      `beyondwords-visibility-${articleId}`,
      handleVisibilityChange as EventListener,
    );

    if (window.beyondWordsPlayerInstances?.[articleId]) {
      setIsPlayerReady(true);
    }

    return () => {
      window.removeEventListener(`beyondwords-ready-${articleId}`, handleReady);
      window.removeEventListener(
        `beyondwords-visibility-${articleId}`,
        handleVisibilityChange as EventListener,
      );
    };
  }, [articleId]);

  const toggleTts = useCallback(() => {
    const newVisible = !ttsVisibleRef.current;
    setTtsVisible(newVisible);

    // Signal the player about the toggle
    window.dispatchEvent(
      new CustomEvent(`toggle-tts-${articleId}`, {
        detail: { visible: newVisible },
      }),
    );
  }, [articleId]);

  const listenBtnRef = (btn: HTMLButtonElement | null) => {
    if (!btn || typeof document === "undefined") return;

    const widget = document.getElementById(`tts-sticky-widget-${articleId}`);
    if (!widget) return;

    const player = window.beyondWordsPlayerInstances?.[articleId];
    const isPlaying = player?.playbackState === "playing";
    const shouldShow = ttsVisibleRef.current || isPlaying;

    if (shouldShow) {
      fadeInWidget(widget);
    } else {
      widget.classList.remove(VISIBLE_CLASS);
      widget.style.display = "none";
    }
  };

  return { ttsVisible, toggleTts, listenBtnRef, isPlayerReady };
}
